import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Select, 
  message, 
  Popconfirm,
  DatePicker,
  Card,
  Row,
  Col
} from 'antd';
import { 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
  CrownOutlined,
  UserOutlined,
  ExportOutlined
} from '@ant-design/icons';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  fetchAllUsers,
  updateUser,
  softDeleteUser,
  permanentDeleteUser,
} from '../../services/firestoreService';
import { isActivePremium } from '../../utils/userMapper';
import { debugLog, debugPremiumSnapshot } from '../../utils/debugLog';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const UserManagement = () => {
  const { logActivity } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [inactiveFilter, setInactiveFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    loadUsers();
    logActivity('view_users', 'Viewed user management page');
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchText, statusFilter, subscriptionFilter, dateRange, inactiveFilter]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const allUsers = await fetchAllUsers();
      const activeUsers = allUsers.filter((u) => u.status !== 'deleted');
      debugPremiumSnapshot('UserManagement', activeUsers);
      setUsers(activeUsers);
    } catch (error) {
      debugLog('UserManagement', 'Failed to load users', error?.message || error);
      message.error('Failed to load users from Firebase');
    } finally {
      setLoadingUsers(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.fullName?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    // Subscription filter
    if (subscriptionFilter === 'premium') {
      filtered = filtered.filter((user) => isActivePremium(user));
    } else if (subscriptionFilter === 'free') {
      filtered = filtered.filter((user) => !isActivePremium(user));
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filtered = filtered.filter(user => {
        if (!user.registeredAt) return false;
        const userDate = moment(user.registeredAt);
        return userDate.isBetween(start, end, 'day', '[]');
      });
    }

    // Inactive filter
    if (inactiveFilter !== 'all') {
      const now = new Date();
      const daysThreshold = parseInt(inactiveFilter);
      filtered = filtered.filter(user => {
        if (!user.lastLogin) return true; // Never logged in users
        const lastLogin = new Date(user.lastLogin);
        const daysDiff = Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24));
        return daysDiff >= daysThreshold;
      });
    }

    setFilteredUsers(filtered);
    debugLog('UserManagement', 'filter result', {
      subscriptionFilter,
      resultCount: filtered.length,
      premiumInResult: filtered.filter((user) => isActivePremium(user)).length,
    });
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalVisible(true);
    logActivity('view_user_details', `Viewed details for user: ${user.username}`);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      status: user.status,
      subscriptionType: user.subscriptionType,
      subscriptionExpiry: user.subscriptionExpiry ? moment(user.subscriptionExpiry) : null
    });
    setIsEditModalVisible(true);
  };

  const handleSaveEdit = () => {
    editForm.validateFields().then(async (values) => {
      try {
        await updateUser(selectedUser.uid, {
          ...values,
          subscriptionExpiry: values.subscriptionExpiry ? values.subscriptionExpiry.toISOString() : null,
        });
        await loadUsers();
        setIsEditModalVisible(false);
        message.success('User updated successfully');
        logActivity('update_user', `Updated user: ${selectedUser.username}`, {
          userId: selectedUser.username,
          changes: values,
        });
      } catch {
        message.error('Failed to update user');
      }
    });
  };

  const handleSuspendUser = async (user) => {
    try {
      await updateUser(user.uid, { status: 'suspended' });
      await loadUsers();
      message.warning(`User ${user.username} has been suspended`);
      logActivity('suspend_user', `Suspended user: ${user.username}`, { userId: user.username });
    } catch {
      message.error('Failed to suspend user');
    }
  };

  const handleReactivateUser = async (user) => {
    try {
      await updateUser(user.uid, { status: 'active' });
      await loadUsers();
      message.success(`User ${user.username} has been reactivated`);
      logActivity('reactivate_user', `Reactivated user: ${user.username}`, { userId: user.username });
    } catch {
      message.error('Failed to reactivate user');
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      await softDeleteUser(user.uid);
      await loadUsers();
      message.warning(`User ${user.username} has been moved to trash`);
      logActivity('soft_delete_user', `Soft deleted user: ${user.username}`, { userId: user.username });
    } catch {
      message.error('Failed to delete user');
    }
  };

  const handlePermanentDelete = async (user) => {
    try {
      await permanentDeleteUser(user.uid, user.username);
      await loadUsers();
      message.error(`User ${user.username} has been permanently deleted`);
      logActivity('permanent_delete_user', `Permanently deleted user: ${user.username}`, { userId: user.username });
    } catch {
      message.error('Failed to permanently delete user');
    }
  };

  const handleExportCSV = () => {
    const csv = [
      ['Username', 'Email', 'Full Name', 'Status', 'Subscription', 'Registered At'],
      ...filteredUsers.map(user => [
        user.username,
        user.email || '',
        user.fullName || '',
        user.status || 'active',
        isActivePremium(user) ? 'premium' : 'free',
        user.registeredAt ? new Date(user.registeredAt).toLocaleDateString() : ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    logActivity('export_users', `Exported ${filteredUsers.length} users to CSV`);
    message.success('Users exported successfully');
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (username) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
            {username?.charAt(0).toUpperCase()}
          </div>
          <span className="font-medium">{username}</span>
        </div>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || <span className="text-gray-400">N/A</span>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          active: 'green',
          suspended: 'red',
          pending: 'orange'
        };
        return <Tag color={colors[status] || 'default'}>{(status || 'active').toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Subscription',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: (_, record) => {
        if (isActivePremium(record)) {
          return <Tag icon={<CrownOutlined />} color="gold">PREMIUM</Tag>;
        }
        return <Tag color="default">FREE</Tag>;
      }
    },
    {
      title: 'Registered',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(a.registeredAt) - new Date(b.registeredAt)
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date) => {
        if (!date) return <span className="text-gray-400">Never</span>;
        const lastLogin = new Date(date);
        const now = new Date();
        const daysDiff = Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24));
        
        if (daysDiff === 0) return <Tag color="green">Today</Tag>;
        if (daysDiff === 1) return <Tag color="blue">Yesterday</Tag>;
        if (daysDiff <= 7) return <Tag color="cyan">{daysDiff} days ago</Tag>;
        if (daysDiff <= 30) return <Tag color="orange">{daysDiff} days ago</Tag>;
        return <Tag color="red">{daysDiff} days ago</Tag>;
      },
      sorter: (a, b) => new Date(b.lastLogin || 0) - new Date(a.lastLogin || 0)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, user) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewUser(user)}
            className="text-blue-500"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(user)}
            className="text-green-500"
          />
          {user.status === 'suspended' ? (
            <Popconfirm
              title="Reactivate this user?"
              onConfirm={() => handleReactivateUser(user)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                icon={<UnlockOutlined />}
                className="text-orange-500"
              />
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Suspend this user?"
              onConfirm={() => handleSuspendUser(user)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                icon={<LockOutlined />}
                className="text-orange-500"
              />
            </Popconfirm>
          )}
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(user)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-gray-500 mt-1">Manage and monitor all user accounts</p>
        </div>
        <Button
          type="primary"
          icon={<ExportOutlined />}
          onClick={handleExportCSV}
          className="bg-green-500 hover:bg-green-600"
        >
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card className="text-center">
            <div className="text-3xl font-bold text-blue-500">{users.length}</div>
            <div className="text-gray-500 mt-1">Total Users</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="text-center">
            <div className="text-3xl font-bold text-green-500">
              {users.filter(u => u.status !== 'suspended').length}
            </div>
            <div className="text-gray-500 mt-1">Active Users</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="text-center">
            <div className="text-3xl font-bold text-yellow-500">
              {users.filter((u) => isActivePremium(u)).length}
            </div>
            <div className="text-gray-500 mt-1">Premium Users</div>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card>
        <Space size="middle" wrap>
          <Input
            placeholder="Search by username, email, or name..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
          >
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="suspended">Suspended</Option>
            <Option value="pending">Pending</Option>
          </Select>
          <Select
            value={subscriptionFilter}
            onChange={setSubscriptionFilter}
            style={{ width: 180 }}
          >
            <Option value="all">All Subscriptions</Option>
            <Option value="free">Free</Option>
            <Option value="premium">Premium</Option>
          </Select>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder={['Start Date', 'End Date']}
            style={{ width: 280 }}
            allowClear
          />
          <Select
            value={inactiveFilter}
            onChange={setInactiveFilter}
            style={{ width: 180 }}
          >
            <Option value="all">All Activity</Option>
            <Option value="7">Inactive 7+ days</Option>
            <Option value="30">Inactive 30+ days</Option>
            <Option value="60">Inactive 60+ days</Option>
          </Select>
        </Space>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="uid"
          loading={loadingUsers}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} users` }}
          scroll={{ x: true }}
        />
      </Card>

      {/* View User Modal */}
      <Modal
        title="User Details"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 pb-4 border-b">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-2xl font-bold">
                {selectedUser.username?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-xl font-bold">{selectedUser.username}</h3>
                <p className="text-gray-500">{selectedUser.email || 'No email'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-sm">Full Name</p>
                <p className="font-semibold">{selectedUser.fullName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Status</p>
                <Tag color={selectedUser.status === 'active' ? 'green' : 'red'}>
                  {(selectedUser.status || 'active').toUpperCase()}
                </Tag>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Subscription</p>
                <Tag color={isActivePremium(selectedUser) ? 'gold' : 'default'}>
                  {isActivePremium(selectedUser) ? 'PREMIUM' : 'FREE'}
                </Tag>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Registered At</p>
                <p className="font-semibold">
                  {selectedUser.registeredAt ? new Date(selectedUser.registeredAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Last Login</p>
                <p className="font-semibold">
                  {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleString() : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">IP Address</p>
                <p className="font-semibold">{selectedUser.ipAddress || 'N/A'}</p>
              </div>
              {selectedUser.subscriptionExpiry && (
                <div>
                  <p className="text-gray-500 text-sm">Subscription Expiry</p>
                  <p className="font-semibold">
                    {new Date(selectedUser.subscriptionExpiry).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onOk={handleSaveEdit}
        okText="Save Changes"
        width={600}
      >
        <Form form={editForm} layout="vertical" className="mt-4">
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: 'Username is required' }]}
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: 'email', message: 'Invalid email' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Full Name" name="fullName">
            <Input />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="active">Active</Option>
              <Option value="suspended">Suspended</Option>
              <Option value="pending">Pending</Option>
            </Select>
          </Form.Item>

          {/* <Form.Item
            label="Subscription Type"
            name="subscriptionType"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="free">Free</Option>
              <Option value="premium">Premium</Option>
            </Select>
          </Form.Item> */}

          {/* <Form.Item label="Subscription Expiry" name="subscriptionExpiry">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item> */}
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
